import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { tendenciasEnVivo, getTopPorCategoria, tiposTendencia, direccionConfig, reglasTendencias, statsTendencias, formatearNumero, type TipoTendencia, type TendenciaEnVivo } from '@/mocks/tendenciasEnVivo';

function TendenciaCard({ tendencia }: { tendencia: TendenciaEnVivo }) {
  const tipoInfo = tiposTendencia.find(t => t.id === tendencia.tipo);
  const dirInfo = direccionConfig[tendencia.direccion];

  return (
    <div className="group bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden hover:border-background-300/60 transition-all cursor-pointer">
      <div className="relative h-40 overflow-hidden">
        <img src={tendencia.imagenUrl} alt={tendencia.nombre} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-foreground-950/10 to-transparent"></div>
        <div className="absolute top-3 left-3 flex items-center gap-1.5">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${tipoInfo?.color} ${tipoInfo?.bg} bg-white/90`}>
            <i className={`${tipoInfo?.icono} text-[9px]`}></i>
            {tipoInfo?.label}
          </span>
          {tendencia.direccion === 'explosivo' && (
            <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-rose-500 text-white text-[9px] font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
              EXPLOSIVO
            </span>
          )}
        </div>
        <div className="absolute top-3 right-3">
          <span className="px-2 py-0.5 rounded-md bg-foreground-950/60 backdrop-blur-sm text-white text-[10px] font-bold">#{tendencia.posicion}</span>
        </div>
        <div className="absolute bottom-3 left-3 right-3">
          <h3 className="text-sm font-bold text-white truncate drop-shadow-md">{tendencia.nombre}</h3>
        </div>
      </div>

      <div className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <img src={tendencia.logoUrl} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-xs text-foreground-700 truncate">{tendencia.subtitulo}</p>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${dirInfo.textoColor}`}>
                <i className={`${dirInfo.icono} text-[9px]`}></i>
                {dirInfo.label}
              </span>
              <span className="text-[10px] text-foreground-500">+{tendencia.crecimiento}%</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4 text-[10px] text-foreground-500">
          {tendencia.senalesActividad.oyentes > 0 && (
            <span className="flex items-center gap-1">
              <i className="ri-headphone-line text-[9px]"></i>
              {formatearNumero(tendencia.senalesActividad.oyentes)}
            </span>
          )}
          <span className="flex items-center gap-1">
            <i className="ri-chat-1-line text-[9px]"></i>
            {formatearNumero(tendencia.senalesActividad.comentarios)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-share-forward-line text-[9px]"></i>
            {formatearNumero(tendencia.senalesActividad.compartidos)}
          </span>
        </div>

        {tendencia.badge && (
          <span className="inline-block px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[9px] font-semibold">{tendencia.badge}</span>
        )}
      </div>
    </div>
  );
}

function FeaturedTendenciaCard({ tendencia }: { tendencia: TendenciaEnVivo }) {
  const tipoInfo = tiposTendencia.find(t => t.id === tendencia.tipo);
  const dirInfo = direccionConfig[tendencia.direccion];

  return (
    <div className="group relative h-[280px] rounded-2xl overflow-hidden bg-background-100 cursor-pointer">
      <img src={tendencia.imagenUrl} alt={tendencia.nombre} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/20 to-transparent"></div>

      <div className="absolute top-4 left-4 flex items-center gap-2">
        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold border ${tipoInfo?.color} ${tipoInfo?.bg} bg-white/90`}>
          <i className={`${tipoInfo?.icono} text-[9px]`}></i>
          {tipoInfo?.label}
        </span>
        {tendencia.direccion === 'explosivo' && (
          <span className="inline-flex items-center gap-0.5 px-2.5 py-1 rounded-full bg-rose-500 text-white text-[9px] font-bold">
            <i className="ri-fire-line text-[9px]"></i>
            EXPLOSIVO
          </span>
        )}
      </div>

      <div className="absolute top-4 right-4">
        <span className="flex items-center justify-center w-9 h-9 rounded-full bg-foreground-950/60 backdrop-blur-sm text-white text-sm font-bold">#{tendencia.posicion}</span>
      </div>

      <div className="absolute bottom-0 left-0 right-0 p-6">
        <div className="flex items-center gap-3 mb-3">
          <img src={tendencia.logoUrl} alt="" className="w-10 h-10 rounded-full object-cover ring-2 ring-white/20" />
          <div>
            <h3 className="text-xl font-bold text-white drop-shadow-md">{tendencia.nombre}</h3>
            <p className="text-sm text-white/70 mt-0.5">{tendencia.subtitulo}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4 text-xs text-white/60">
          <span className={`inline-flex items-center gap-1 font-semibold ${dirInfo.textoColor} bg-white/10 px-2 py-0.5 rounded-full`}>
            <i className={`${dirInfo.icono} text-[10px]`}></i>
            {dirInfo.label} +{tendencia.crecimiento}%
          </span>
          {tendencia.estaEnVivo && (
            <span className="inline-flex items-center gap-1 text-emerald-300 font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
              EN VIVO
            </span>
          )}
          {tendencia.senalesActividad.oyentes > 0 && (
            <span className="flex items-center gap-1">
              <i className="ri-headphone-line"></i>
              {formatearNumero(tendencia.senalesActividad.oyentes)}
            </span>
          )}
          <span className="flex items-center gap-1">
            <i className="ri-chat-1-line"></i>
            {formatearNumero(tendencia.senalesActividad.comentarios)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-share-forward-line"></i>
            {formatearNumero(tendencia.senalesActividad.compartidos)}
          </span>
        </div>

        {tendencia.badge && (
          <span className="inline-block mt-2 px-2.5 py-0.5 rounded-full bg-rose-500/80 text-white text-[10px] font-semibold">{tendencia.badge}</span>
        )}
      </div>
    </div>
  );
}

function ReglaCard({ regla }: { regla: typeof reglasTendencias[0] }) {
  return (
    <div className={`bg-background-50 rounded-2xl border ${regla.colorBorde} p-5 space-y-3`}>
      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${regla.gradiente} flex items-center justify-center`}>
        <i className={`${regla.icono} text-white text-lg`}></i>
      </div>
      <h4 className="text-sm font-bold text-foreground-900">{regla.titulo}</h4>
      <p className="text-xs text-foreground-600 leading-relaxed">{regla.descripcion}</p>
    </div>
  );
}

export default function TendenciasEnVivoSection() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TipoTendencia | 'todas' | 'reglas'>('todas');
  const topPorCat = getTopPorCategoria();

  const filteredTendencias = activeTab === 'todas'
    ? tendenciasEnVivo.slice(0, 12)
    : activeTab === 'reglas'
    ? []
    : tendenciasEnVivo.filter(t => t.tipo === activeTab).slice(0, 4);

  const top3 = tendenciasEnVivo.slice(0, 3);

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-100 text-rose-700 text-xs font-bold mb-4">
            <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse"></span>
            TENDENCIAS EN VIVO
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-3">Lo Que Está Pasando Ahora</h2>
          <p className="text-base text-foreground-600 max-w-2xl mx-auto">Tendencias calculadas en tiempo real basadas en actividad de la comunidad. Radios, programas, locutores, eventos y clubes — lo más caliente del momento.</p>

          {/* Mini stats */}
          <div className="flex flex-wrap justify-center gap-6 mt-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-foreground-950">{statsTendencias.totalTendencias}</p>
              <p className="text-xs text-foreground-500">Tendencias</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-rose-600">{statsTendencias.tendenciasExplosivas}</p>
              <p className="text-xs text-foreground-500">Explosivas</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-foreground-950">{statsTendencias.categoriasActivas}</p>
              <p className="text-xs text-foreground-500">Categorías</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-600">{statsTendencias.actualizacionesUltimaHora}</p>
              <p className="text-xs text-foreground-500">Updates/h</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {[
            { id: 'todas' as const, label: 'Todas', icono: 'ri-fire-line' },
            ...tiposTendencia.map(t => ({ id: t.id, label: t.label, icono: t.icono })),
            { id: 'reglas' as const, label: 'Reglas', icono: 'ri-scales-line' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all cursor-pointer ${
                activeTab === tab.id
                  ? 'bg-rose-500 text-white'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
              }`}
            >
              <i className={`${tab.icono} text-xs`}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Featured Top 3 — solo en tab Todas */}
        {activeTab === 'todas' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-10">
            {top3.map(t => (
              <FeaturedTendenciaCard key={t.id} tendencia={t} />
            ))}
          </div>
        )}

        {/* Grid de cards */}
        {activeTab !== 'reglas' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filteredTendencias.map(t => (
              <TendenciaCard key={t.id} tendencia={t} />
            ))}
          </div>
        )}

        {/* Vista Reglas */}
        {activeTab === 'reglas' && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {reglasTendencias.map(r => (
                <ReglaCard key={r.id} regla={r} />
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-10 text-center">
          <button
            onClick={() => navigate('/tendencias-en-vivo')}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-rose-500 to-amber-500 text-white font-bold text-sm hover:from-rose-600 hover:to-amber-600 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-fire-line"></i>
            Ver Todas las Tendencias en Vivo
          </button>
        </div>

        {/* Regla final */}
        <div className="mt-8 text-center">
          <p className="text-xs text-foreground-400 italic">"Basado en actividad real, no manual. La comunidad define qué es tendencia."</p>
        </div>
      </div>
    </section>
  );
}